import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const ROLES_VISIBLE = [
  { value: 'admin',         label: 'Admin',         hint: 'Full access to everything' },
  { value: 'executive',     label: 'Executive',     hint: 'Cross-program overview (Karen)' },
  { value: 'regional_ops',  label: 'Regional Ops',  hint: 'Multi-site dispatch + triage' },
  { value: 'site_manager',  label: 'Site Manager',  hint: 'Single-site ticket queue' },
  { value: 'field_tech',    label: 'Field Tech',    hint: 'Dispatched ticket worker' },
  { value: 'remote_tech',   label: 'Remote Tech',   hint: 'Remote session operator' },
]
// 'user' and 'auditor' are legacy — accepted by DB but hidden from UI dropdown

// Keep in sync with TEAM_OPTIONS in Tickets.js / TicketDetail.js.
const TEAM_OPTIONS = ['field_service','remote_team','migration_pm','ncs','nxt_product','ops']

const API_URL = process.env.REACT_APP_SUPABASE_URL + '/functions/v1/admin-users'

async function callAdminApi(action, params = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not signed in')

  const res = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      action,
      redirect_to: window.location.origin,
      ...params,
    }),
  })
  const text = await res.text()
  let body; try { body = JSON.parse(text) } catch { body = { error: text } }
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`)
  return body
}

function roleLabel(role) {
  const found = ROLES_VISIBLE.find(r => r.value === role)
  return found?.label || role
}

function timeAgo(isoStr) {
  if (!isoStr) return '—'
  const ms = Date.now() - new Date(isoStr).getTime()
  const d = Math.floor(ms / 86400000)
  if (d === 0) return 'today'
  if (d === 1) return 'yesterday'
  if (d < 30) return `${d}d ago`
  if (d < 365) return `${Math.floor(d / 30)}mo ago`
  return `${Math.floor(d / 365)}y ago`
}

export default function UserManagement({ currentUserId }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyUserId, setBusyUserId] = useState(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [toast, setToast] = useState(null)

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000) }

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const { users } = await callAdminApi('list')
      // Enrich with 'team' — the admin edge function doesn't return it today,
      // and we don't want to change the edge function source to avoid cross-repo
      // coordination. The profiles table is readable to authenticated users via
      // the existing 'auth read profiles' policy, so a direct select is fine.
      const ids = users.map(u => u.id)
      let teamById = {}
      if (ids.length > 0) {
        const { data: teams } = await supabase
          .from('profiles').select('id, team').in('id', ids)
        teamById = Object.fromEntries((teams || []).map(r => [r.id, r.team]))
      }
      setUsers(users.map(u => ({ ...u, team: teamById[u.id] || null })))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleSetRole(user, newRole) {
    if (newRole === user.role) return
    if (user.id === currentUserId && newRole !== 'admin') {
      if (!window.confirm('Demote yourself? You will lose admin access immediately. Continue?')) return
    }
    setBusyUserId(user.id)
    try {
      await callAdminApi('set_role', { user_id: user.id, role: newRole })
      flash(`Role updated for ${user.email}`)
      await load()
    } catch (e) {
      alert(`Failed: ${e.message}`)
    } finally {
      setBusyUserId(null)
    }
  }

  async function handleSetTeam(user, newTeam) {
    // newTeam is '' for "no team", else one of TEAM_OPTIONS.
    const normalized = newTeam || null
    if (normalized === (user.team || null)) return  // no-op if unchanged
    setBusyUserId(user.id)
    try {
      // Direct UPDATE on profiles; RLS policy 'admin updates any profile'
      // from 20260421_user_management.sql permits this for admin role.
      const { error } = await supabase
        .from('profiles').update({ team: normalized }).eq('id', user.id)
      if (error) throw new Error(error.message)
      flash(normalized
        ? `${user.email} assigned to ${normalized.replace(/_/g,' ')}`
        : `${user.email} removed from team`)
      // Optimistic local update so the row re-renders instantly
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, team: normalized } : u))
    } catch (e) {
      alert(`Failed: ${e.message}`)
    } finally {
      setBusyUserId(null)
    }
  }

  async function handleDeactivate(user) {
    if (user.id === currentUserId) { alert('Cannot deactivate yourself'); return }
    if (!window.confirm(`Deactivate ${user.email}? They will not be able to sign in. You can reactivate later.`)) return
    setBusyUserId(user.id)
    try {
      await callAdminApi('deactivate', { user_id: user.id })
      flash(`${user.email} deactivated`)
      await load()
    } catch (e) { alert(`Failed: ${e.message}`) }
    finally { setBusyUserId(null) }
  }

  async function handleReactivate(user) {
    setBusyUserId(user.id)
    try {
      await callAdminApi('reactivate', { user_id: user.id })
      flash(`${user.email} reactivated`)
      await load()
    } catch (e) { alert(`Failed: ${e.message}`) }
    finally { setBusyUserId(null) }
  }

  async function handleResetPassword(user) {
    if (!window.confirm(`Send password reset email to ${user.email}?`)) return
    setBusyUserId(user.id)
    try {
      await callAdminApi('reset_password', { email: user.email })
      flash(`Password reset email sent to ${user.email}`)
    } catch (e) { alert(`Failed: ${e.message}`) }
    finally { setBusyUserId(null) }
  }

  async function handleInvite(payload) {
    try {
      await callAdminApi('invite', payload)
      setInviteOpen(false)
      flash(`Invitation sent to ${payload.email}`)
      await load()
    } catch (e) { alert(`Failed: ${e.message}`) }
  }

  if (loading) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading users…</div>

  if (error) return (
    <div style={{ padding: 24 }}>
      <div className="card" style={{ borderColor: 'var(--red)', background: 'var(--red-light)', padding: 16 }}>
        <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>Could not load users</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{error}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          If this says <code>forbidden</code>, your profile role is not <code>admin</code>.{' '}
          If it says <code>Function not found</code>, the <code>admin-users</code> Edge Function needs to be deployed in Supabase.
        </div>
        <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
      </div>
    </div>
  )

  const actives = users.filter(u => u.active !== false)
  const deactives = users.filter(u => u.active === false)

  return (
    <div style={{ padding: '8px 0' }}>
      {toast && (
        <div style={{ background: 'var(--green-light)', color: 'var(--green)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          ✓ {toast}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {actives.length} active, {deactives.length} deactivated
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setInviteOpen(true)}>
          + Invite User
        </button>
      </div>

      <UserTable
        users={actives}
        title="Active"
        currentUserId={currentUserId}
        busyUserId={busyUserId}
        onSetRole={handleSetRole}
        onSetTeam={handleSetTeam}
        onDeactivate={handleDeactivate}
        onResetPassword={handleResetPassword}
      />

      {deactives.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <UserTable
            users={deactives}
            title="Deactivated"
            deactivated
            currentUserId={currentUserId}
            busyUserId={busyUserId}
            onReactivate={handleReactivate}
          />
        </div>
      )}

      {inviteOpen && (
        <InviteModal onClose={() => setInviteOpen(false)} onSubmit={handleInvite} />
      )}
    </div>
  )
}

function UserTable({ users, title, deactivated, currentUserId, busyUserId, onSetRole, onSetTeam, onDeactivate, onReactivate, onResetPassword }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {title} ({users.length})
      </div>
      {users.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>No users.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-muted)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Name / Email</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Team</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Last sign-in</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = u.id === currentUserId
                const busy = busyUserId === u.id
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 500 }}>
                        {u.full_name || '—'} {isSelf && <span style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, marginLeft: 6 }}>(you)</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email || '—'}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {deactivated ? (
                        <span style={{ fontSize: 13 }}>{roleLabel(u.role)}</span>
                      ) : (
                        <select
                          value={u.role || ''}
                          disabled={busy}
                          onChange={(e) => onSetRole(u, e.target.value)}
                          style={{ fontSize: 13, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)' }}
                        >
                          {/* Include current value even if not in visible list (legacy auditor/user) */}
                          {!ROLES_VISIBLE.find(r => r.value === u.role) && u.role && (
                            <option value={u.role}>{u.role} (legacy)</option>
                          )}
                          {ROLES_VISIBLE.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {deactivated ? (
                        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          {u.team ? u.team.replace(/_/g,' ') : '—'}
                        </span>
                      ) : (
                        <select
                          value={u.team || ''}
                          disabled={busy || !onSetTeam}
                          onChange={(e) => onSetTeam && onSetTeam(u, e.target.value)}
                          style={{ fontSize: 13, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)' }}
                        >
                          <option value="">— None —</option>
                          {TEAM_OPTIONS.map(t => (
                            <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                      {timeAgo(u.last_sign_in_at)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {deactivated ? (
                        <button className="btn btn-sm" disabled={busy} onClick={() => onReactivate(u)}>
                          Reactivate
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <button className="btn btn-sm" disabled={busy || !u.email} onClick={() => onResetPassword(u)}>
                            Send reset
                          </button>
                          {!isSelf && (
                            <button className="btn btn-sm" disabled={busy} onClick={() => onDeactivate(u)} style={{ color: 'var(--red)' }}>
                              Deactivate
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function InviteModal({ onClose, onSubmit }) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState('field_tech')
  const [submitting, setSubmitting] = useState(false)

  async function submit(e) {
    e.preventDefault()
    if (!email) return
    setSubmitting(true)
    try {
      await onSubmit({ email: email.trim(), full_name: fullName.trim(), role })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card" style={{ maxWidth: 440, width: '100%', padding: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Invite user</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          They'll get an email with a magic link to set a password and sign in.
        </div>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>EMAIL</label>
            <input
              type="email" required autoFocus value={email} onChange={e => setEmail(e.target.value)}
              placeholder="name@mcww.com"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>FULL NAME (optional)</label>
            <input
              type="text" value={fullName} onChange={e => setFullName(e.target.value)}
              placeholder="Julie Smith"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>ROLE</label>
            <select
              value={role} onChange={e => setRole(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--bg)' }}
            >
              {ROLES_VISIBLE.map(r => (
                <option key={r.value} value={r.value}>
                  {r.label} — {r.hint}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !email}>
              {submitting ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
